/**
 * /locations — Office Locations & Reviews Page
 *
 * SEO + backlink purpose:
 *  - Schema.org AccountingService markup per office (rich-result eligible)
 *  - Backlink target for KGOB's Google Business Profile listings →
 *    set each GBP listing's Website URL to https://kgob.com/locations#office-id
 *  - Each card embeds a Google Maps iframe (no API key required) and offers
 *    "Get Directions" + "Leave a Review" CTAs to drive Google review volume.
 *
 * Distinct from /contact (which is lead-form focused). This page is the
 * SEO / local-discovery / review-collection surface.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { MapPin, Phone, Clock, ArrowUpRight, Star } from 'lucide-react';
import { useJsonLd } from '@/hooks/useJsonLd';

// ---------- Location-specific FAQ data ----------
// Optimized for Google's location-page rich-result eligibility and Map Pack ranking.
const LOCATION_FAQS = [
  {
    q: 'Do you accept walk-ins?',
    a: 'Walk-ins are welcome at our Charlotte HQ on East WT Harris Blvd during business hours (Mon–Fri 9 AM – 6 PM, Sat 10 AM – 2 PM). For the Winston-Salem, SouthPark, Mooresville, and Pittsburgh offices, we ask that visits be scheduled in advance so a partner is available for you.',
  },
  {
    q: 'Can I drop off documents at any of your offices?',
    a: 'Yes. You can drop off tax documents, audit responses, signature pages, and engagement files at any of our five offices during business hours. Charlotte HQ also has a secure after-hours drop slot at the Suite 200 entrance. For sensitive items, we recommend using our encrypted client portal — it is faster and trackable.',
  },
  {
    q: 'Do I have to be near one of your offices to work with KGOB?',
    a: 'Not at all. While our five physical offices anchor us in Charlotte, Winston-Salem, the Lake Norman corridor, and Pittsburgh, we serve clients in all 50 states and welcome new clients from any city. Tax preparation, advisory, controversy, IRS resolution, valuations, entity work, and bookkeeping are all delivered remotely as smoothly as they are in person. Your address has no bearing on whether we can help you.',
  },
  {
    q: 'How do I schedule an appointment?',
    a: 'Call our team directly at 1-844-599-3355 (Mon–Fri 9 AM – 6 PM EST). A team member will confirm office availability, the right partner for your need, and the right time. You can also send a message via our Contact page if calling during business hours is not convenient.',
  },
  {
    q: 'Is there free parking at your offices?',
    a: 'Yes. Free surface parking is available at the Charlotte HQ (Suite 200, East WT Harris Blvd) and the Mooresville office. SouthPark has validated parking in the Suite 1200 garage. The Winston-Salem office has metered street parking on Main Street plus a public deck one block north.',
  },
  {
    q: 'Are your offices wheelchair accessible?',
    a: 'Yes. All five KGOB offices are ADA-accessible with ground-level or elevator entry, accessible parking, and accessible restrooms. If you have specific accessibility needs we can prepare for in advance, please mention them when you call.',
  },
  {
    q: 'Do you offer virtual meetings if I cannot visit in person?',
    a: 'Yes. Every service we provide — tax preparation, controversy, advisory, valuations, IRS resolution, and entity work — can be delivered remotely. We use Zoom for video meetings, a secure document portal for file exchange, and e-signature for engagement letters. Many of our long-standing clients have never visited a physical office.',
  },
  {
    q: 'Can I bring a family member, attorney, or translator to my meeting?',
    a: 'Of course. You are welcome to bring anyone who helps you make sound decisions — a spouse, adult child, attorney, financial advisor, or translator. If there is anything we can do to make the meeting smoother (a private conference room, specific materials in advance, accessibility accommodations), let us know when you book.',
  },
];

// ---------- Office data ----------
const OFFICES = [
  {
    id: 'charlotte-hq',
    city: 'Charlotte',
    neighborhood: 'University',
    role: 'Main Office',
    address1: '2740 East WT Harris Blvd',
    address2: 'Suite 200',
    cityState: 'Charlotte, NC 28213',
    phone: '+1-704-599-3355',
    phoneDisplay: '(704) 599-3355',
    hours: [
      { d: 'Monday – Friday', t: '9:00 AM – 6:00 PM' },
      { d: 'Saturday', t: '10:00 AM – 2:00 PM' },
      { d: 'Sunday', t: 'By appointment' },
    ],
    googleMaps: 'https://maps.app.goo.gl/ivNjbD5tGZ1J8YW4A',
    geo: { lat: 35.2775429, lng: -80.7276954 },
    hasReviews: true,
    note: 'Our flagship office and team headquarters. Walk-ins welcome during business hours.',
  },
  {
    id: 'winston-salem',
    city: 'Winston-Salem',
    neighborhood: 'Downtown',
    role: 'Triad Office',
    address1: '326 South Main Street',
    address2: '',
    cityState: 'Winston-Salem, NC 27101',
    phone: '+1-336-761-0366',
    phoneDisplay: '(336) 761-0366',
    hours: [
      { d: 'Monday – Friday', t: '9:00 AM – 5:00 PM' },
      { d: 'Saturday – Sunday', t: 'By appointment' },
    ],
    googleMaps: 'https://maps.app.goo.gl/Hk5YfVMiNR6sD4s5A',
    geo: { lat: 36.0914455, lng: -80.2430443 },
    hasReviews: true,
    note: 'Serving the Triad with the same standards as our Charlotte offices.',
  },
  {
    id: 'southpark',
    city: 'Charlotte',
    neighborhood: 'SouthPark',
    role: 'By Appointment',
    address1: '6000 Fairview Road',
    address2: 'Suite 1200',
    cityState: 'Charlotte, NC 28210',
    phone: '+1-704-599-3355',
    phoneDisplay: '(704) 599-3355',
    hours: [
      { d: 'By appointment', t: 'Mon – Fri' },
    ],
    googleMaps: 'https://www.google.com/maps/place/Kohari+Gonzalez+Oneyear+%26+Brown+PLLC/@35.1502976,-81.9938999,9z/data=!4m10!1m2!2m1!1sKohari+Gonzalez+Oneyear+%26+Brown+PLLC!3m6!1s0x88569f06533c2d93:0xe872b0cf6adf964c!8m2!3d35.1502976!4d-80.8403354!15sCiRLb2hhcmkgR29uemFsZXogT25leWVhciAmIEJyb3duIFBMTEOSARtjZXJ0aWZpZWRfcHVibGljX2FjY291bnRhbnTgAQA!16s%2Fg%2F11z5pmkylr?entry=ttu',
    geo: { lat: 35.149, lng: -80.825 },
    hasReviews: false,
    note: 'Available for client meetings and signing appointments by request.',
  },
  {
    id: 'mooresville',
    city: 'Mooresville',
    neighborhood: 'Lake Norman',
    role: 'By Appointment',
    address1: '121 Rolling Hill Road',
    address2: '',
    cityState: 'Mooresville, NC 28117',
    phone: '+1-704-663-6213',
    phoneDisplay: '(704) 663-6213',
    hours: [
      { d: 'By appointment', t: 'Mon – Fri' },
    ],
    googleMaps: 'https://www.google.com/maps/place/Kohari+Gonzalez+Oneyear+%26+Brown+PLLC/@35.5927167,-80.8649103,17z/data=!3m1!4b1!4m6!3m5!1s0x885154e6f8628865:0xee1852291a26c59d!8m2!3d35.5927124!4d-80.8623354!16s%2Fg%2F1tftrssr?entry=ttu',
    geo: { lat: 35.585, lng: -80.810 },
    hasReviews: false,
    note: 'Serving the Lake Norman corridor by appointment.',
  },
  {
    id: 'pittsburgh',
    city: 'Pittsburgh',
    neighborhood: 'Green Tree',
    role: 'By Appointment',
    address1: '651 Holiday Drive',
    address2: 'Plaza 5 Suite 400',
    cityState: 'Pittsburgh, PA 15220',
    phone: '+1-412-254-6280',
    phoneDisplay: '(412) 254-6280',
    hours: [
      { d: 'By appointment', t: 'Mon – Fri' },
    ],
    googleMaps: 'https://www.google.com/maps/search/?api=1&query=651+Holiday+Drive+Plaza+5+Suite+400+Pittsburgh+PA+15220',
    geo: { lat: 40.4193, lng: -80.0568 },
    hasReviews: false,
    note: 'Our first Pennsylvania location, serving Pittsburgh by appointment.',
  },
];

// ---------- LocalBusiness + FAQPage JSON-LD (stacked schema) ----------
function buildSchema() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      ...OFFICES.map((o) => ({
        '@type': 'AccountingService',
        '@id': `https://kgob.com/locations#${o.id}`,
        name: `Kohari Gonzalez Oneyear & Brown PLLC — ${o.city}${o.neighborhood ? ' ' + o.neighborhood : ''}`,
        image: 'https://kgob.com/share/og/v1/kgob-share-default.png',
        url: `https://kgob.com/locations#${o.id}`,
        telephone: o.phone,
        priceRange: '$$$',
        address: {
          '@type': 'PostalAddress',
          streetAddress: [o.address1, o.address2].filter(Boolean).join(', '),
          addressLocality: o.cityState.split(',')[0].trim(),
          addressRegion: o.cityState.split(',')[1].trim().split(' ')[0],
          postalCode: o.cityState.match(/\d{5}/)?.[0] || '',
          addressCountry: 'US',
        },
        geo: { '@type': 'GeoCoordinates', latitude: o.geo.lat, longitude: o.geo.lng },
        hasMap: o.googleMaps,
        parentOrganization: {
          '@type': 'Organization',
          name: 'Kohari Gonzalez Oneyear & Brown PLLC',
          url: 'https://kgob.com',
        },
      })),
      {
        '@type': 'FAQPage',
        '@id': 'https://kgob.com/locations#faq',
        mainEntity: LOCATION_FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };
}

// ---------- Card ----------
function OfficeCard({ office, eager }) {
  const embedSrc = `https://maps.google.com/maps?q=${encodeURIComponent(
    [office.address1, office.address2, office.cityState].filter(Boolean).join(', ')
  )}&t=&z=14&ie=UTF8&iwloc=&output=embed`;

  return (
    <article
      id={office.id}
      className="group bg-[#0F2A4D] border border-white/8 rounded-2xl overflow-hidden hover:border-[#D4AF37]/40 transition-all duration-500 scroll-mt-24"
      data-testid={`office-card-${office.id}`}
    >
      <div className="relative aspect-[16/9] bg-[#0B2240] overflow-hidden">
        <iframe
          src={embedSrc}
          loading={eager ? 'eager' : 'lazy'}
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          className="w-full h-full border-0 opacity-90 group-hover:opacity-100 transition-opacity duration-500"
          title={`Map of KGOB ${office.city} ${office.neighborhood} office`}
        />
        <div className="absolute top-4 left-4 bg-[#0B2240]/85 backdrop-blur-md px-3 py-1 rounded-full border border-[#D4AF37]/30">
          <span className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#D4AF37]" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
            {office.role}
          </span>
        </div>
      </div>

      <div className="p-7 space-y-5">
        <div>
          <h2 className="text-2xl font-bold text-white leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
            {office.city}
            <span className="text-[#2FA3B5] font-semibold"> · {office.neighborhood}</span>
          </h2>
          <p className="text-sm text-white/60 mt-2 leading-relaxed italic font-serif">{office.note}</p>
        </div>

        <address className="not-italic flex items-start gap-3 text-white/85">
          <MapPin size={18} className="mt-0.5 text-[#D4AF37] flex-shrink-0" />
          <div className="text-sm leading-relaxed">
            <div>{office.address1}</div>
            {office.address2 && <div>{office.address2}</div>}
            <div>{office.cityState}</div>
          </div>
        </address>

        <a
          href={`tel:${office.phone}`}
          className="flex items-center gap-3 text-white/85 hover:text-white transition-colors group/phone"
          data-testid={`locations-phone-${office.id}`}
        >
          <Phone size={18} className="text-[#D4AF37] flex-shrink-0" />
          <span className="text-sm font-medium tracking-wide group-hover/phone:text-[#D4AF37] transition-colors">
            {office.phoneDisplay}
          </span>
        </a>

        <div className="flex items-start gap-3 text-white/75">
          <Clock size={18} className="mt-0.5 text-[#D4AF37] flex-shrink-0" />
          <div className="text-xs space-y-0.5">
            {(office.hours || []).map((h, i) => (
              <div key={i} className="flex gap-2">
                <span className="text-white/55">{h.d}</span>
                <span className="text-white/85">{h.t}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={`grid ${office.hasReviews ? 'grid-cols-2' : 'grid-cols-1'} gap-3 pt-3 border-t border-white/8`}>
          <a
            href={office.googleMaps}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] text-xs font-bold py-3 rounded-xl hover:brightness-95 transition-all shadow-[inset_0_1px_0_rgba(255,255,255,0.35)]"
            style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
            data-testid={`locations-directions-${office.id}`}
          >
            Get Directions <ArrowUpRight size={14} strokeWidth={2.5} />
          </a>
          {office.hasReviews && (
            <a
              href={office.googleMaps}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 hover:border-[#D4AF37]/50 text-white text-xs font-semibold py-3 rounded-xl transition-all"
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
              data-testid={`locations-review-${office.id}`}
            >
              <Star size={14} className="text-[#D4AF37]" fill="currentColor" /> Leave a Review
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

export default function Locations() {
  // Imperatively inject schema (Helmet v2 silently drops <script> content)
  useJsonLd('locations-main', buildSchema());

  return (
    <div className="min-h-screen bg-[#0B2240] text-white">
      <Helmet>
        <title>Office Locations · Kohari Gonzalez Oneyear &amp; Brown CPAs &amp; Advisors</title>
        <meta
          name="description"
          content="Five offices across North Carolina and Pennsylvania — Charlotte, Winston-Salem, Lake Norman, and Pittsburgh. Serving clients in all 50 states. Get directions, call us, or leave a review."
        />
        <link rel="canonical" href="https://kgob.com/locations" />
        <meta property="og:title" content="KGOB Office Locations · Charlotte, Winston-Salem &amp; Lake Norman" />
        <meta
          property="og:description"
          content="Five offices serving Charlotte, Winston-Salem, Lake Norman, and Pittsburgh. Luxury Advisory. Precision Compliance."
        />
        <meta property="og:url" content="https://kgob.com/locations" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://kgob.com/share/og/v1/kgob-share-default.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="KGOB Office Locations · Charlotte, Winston-Salem &amp; Lake Norman" />
        <meta name="twitter:description" content="Five offices across North Carolina and Pennsylvania — serving clients in all 50 states." />
        <meta name="twitter:image" content="https://kgob.com/share/og/v1/kgob-twitter-card.png" />
      </Helmet>

      <Header />

      <main className="pb-24">
        <section className="px-4 sm:px-6 lg:px-8 pt-16 pb-12 max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <p
              className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#D4AF37] mb-5"
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
              data-testid="locations-eyebrow"
            >
              Five Offices · One Standard
            </p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="locations-heading"
            >
              Where to find us<span className="text-[#D4AF37]">.</span>
            </h1>
            <p className="text-base sm:text-lg text-white/70 mt-6 leading-relaxed max-w-2xl font-serif">
              Five offices across North Carolina and Pennsylvania. Clients across all 50 states. Stop by, schedule a meeting by phone, or work with us remotely from anywhere — every door opens to the same firm.
            </p>
          </div>
        </section>

        <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7" data-testid="offices-grid">
            {OFFICES.map((o, i) => (
              <OfficeCard key={o.id} office={o} eager={i === 0} />
            ))}
          </div>
        </section>

        {/* Service Area — links to the 4 metro hubs + 2 state pages */}
        <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mt-20" data-testid="locations-service-area">
          <div className="mb-10">
            <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#D4AF37] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
              Service Area · 100+ Cities Across the Carolinas
            </p>
            <h2 className="text-3xl sm:text-4xl text-white leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Whichever way you drive in<span className="text-[#D4AF37]">.</span>
            </h2>
            <p className="text-white/65 text-base mt-5 max-w-3xl leading-relaxed font-serif">
              Five offices anchor our coverage across the Carolinas and Western Pennsylvania. We also work with clients in all 50 states remotely. Choose your region to see the cities we serve.
            </p>
          </div>

          {/* Metro hub grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            {[
              { to: '/locations/charlotte-metro', label: 'Charlotte Metro', sub: 'Charlotte, Concord, Gastonia, Rock Hill, Fort Mill + Upstate SC corridor', count: '27 cities' },
              { to: '/locations/triad', label: 'The Triad', sub: 'Winston-Salem, Greensboro, High Point, Burlington + surrounding', count: '24 cities' },
              { to: '/locations/triangle-piedmont', label: 'The Triangle', sub: 'Raleigh, Durham, Chapel Hill, Cary, Apex + Research Triangle Park', count: '20 cities' },
              { to: '/locations/western-nc-upstate-sc', label: 'Western NC + Upstate SC', sub: 'Asheville, Hickory, Greenville SC, Spartanburg, Anderson', count: '29 cities' },
            ].map((m) => (
              <Link
                key={m.to}
                to={m.to}
                className="group bg-[#0F2A4D] border border-white/10 hover:border-[#D4AF37]/50 rounded-2xl p-6 flex items-start gap-4 transition-all"
                data-testid={`service-area-${m.to.split('/').pop()}`}
              >
                <MapPin size={20} className="text-[#D4AF37] flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <h3 className="text-lg font-bold text-white group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                      {m.label}
                    </h3>
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#D4AF37]/70 flex-shrink-0" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                      {m.count}
                    </span>
                  </div>
                  <p className="text-sm text-white/65 leading-relaxed font-serif">
                    {m.sub}
                  </p>
                  <span className="inline-flex items-center gap-1 mt-3 text-xs font-bold text-[#D4AF37] tracking-wide" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                    Explore region <ArrowUpRight size={12} strokeWidth={2.5} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {/* State-level pages */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              { to: '/north-carolina-cpa', label: 'North Carolina', sub: 'All 100 NC counties · Statewide coverage' },
              { to: '/south-carolina-cpa', label: 'South Carolina', sub: 'Upstate + Midlands + Lowcountry · 46 counties' },
            ].map((s) => (
              <Link
                key={s.to}
                to={s.to}
                className="group bg-gradient-to-br from-[#0F2A4D] to-[#0B2240] border border-[#D4AF37]/15 hover:border-[#D4AF37]/50 rounded-2xl p-6 flex items-center gap-4 transition-all"
                data-testid={`service-area-state-${s.to.replace('/', '')}`}
              >
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#D4AF37] font-bold text-lg" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                    {s.label.split(' ').map((w) => w[0]).join('')}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-white group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                    {s.label} CPA
                  </h3>
                  <p className="text-sm text-white/65 mt-0.5 font-serif">
                    {s.sub}
                  </p>
                </div>
                <ArrowUpRight size={18} className="text-[#D4AF37]/60 group-hover:text-[#D4AF37] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0" strokeWidth={2.5} />
              </Link>
            ))}
          </div>
        </section>

        <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mt-20">
          <div className="bg-gradient-to-br from-[#0F2A4D] to-[#0B2240] border border-[#D4AF37]/20 rounded-2xl p-10 sm:p-14 text-center">
            <h2
              className="text-3xl sm:text-4xl text-white leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Prefer not to drive?
            </h2>
            <p className="text-white/70 text-base mt-4 max-w-xl mx-auto leading-relaxed font-serif">
              Every service we offer can be delivered remotely — secure document portal, video consultations, and signed engagement letters all from where you are.
            </p>
            <div className="mt-8">
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]/90 font-semibold mb-3" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                Appointments by phone
              </p>
              <a
                href="tel:18445993355"
                className="inline-flex items-center gap-3 text-3xl sm:text-4xl font-bold text-white hover:text-[#D4AF37] transition-colors tracking-tight"
                style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
                data-testid="locations-call-cta"
              >
                <Phone size={28} strokeWidth={2.2} className="text-[#D4AF37]" />
                1-844-599-3355
              </a>
              <p className="text-white/55 text-sm mt-3 font-serif italic">
                Mon – Fri · 9:00 AM – 6:00 PM EST · Sat 10 AM – 2 PM
              </p>
              <div className="flex justify-center gap-3 mt-7">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 hover:border-[#D4AF37]/50 text-white font-semibold px-6 py-3 rounded-xl text-sm transition-all"
                  style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
                  data-testid="locations-message-cta"
                >
                  Or send a message
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Frequently Asked Questions ─── */}
        <section className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto mt-20" data-testid="locations-faq-section">
          <div className="mb-12 text-center">
            <p
              className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#D4AF37] mb-4"
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
            >
              Before You Visit
            </p>
            <h2
              className="text-3xl sm:text-4xl text-white leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Questions, answered<span className="text-[#D4AF37]">.</span>
            </h2>
            <p className="text-white/65 text-base mt-5 max-w-2xl mx-auto leading-relaxed font-serif">
              Practical answers about walk-ins, parking, accessibility, document drop-off, and working with us from anywhere in the country.
            </p>
          </div>

          <div className="space-y-3">
            {LOCATION_FAQS.map((f, i) => (
              <details
                key={i}
                className="group bg-[#0F2A4D] border border-white/10 rounded-xl overflow-hidden hover:border-[#D4AF37]/30 transition-colors"
                data-testid={`locations-faq-item-${i}`}
              >
                <summary data-testid="locations-summary-1"
                  className="flex items-start justify-between gap-4 px-6 py-5 cursor-pointer list-none [&::-webkit-details-marker]:hidden"
                  style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
                >
                  <span className="text-white font-semibold text-[15px] leading-snug pr-3">
                    {f.q}
                  </span>
                  <span className="flex-shrink-0 w-7 h-7 rounded-full border border-white/20 group-open:border-[#D4AF37]/60 flex items-center justify-center text-[#D4AF37] transition-all group-open:rotate-45">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-3.5 h-3.5">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 pt-1 -mt-2">
                  <p className="text-white/75 text-[15px] leading-relaxed font-serif" itemProp="text">
                    {f.a}
                  </p>
                </div>
              </details>
            ))}
          </div>

          {/* Bottom-of-FAQ phone fallback */}
          <div className="mt-12 text-center border-t border-white/10 pt-10">
            <p className="text-white/60 text-sm font-serif italic mb-4">
              Still have a question we did not cover?
            </p>
            <a
              href="tel:18445993355"
              className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-white text-base font-bold tracking-tight transition-colors"
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
              data-testid="locations-faq-call-fallback"
            >
              <Phone size={16} strokeWidth={2.5} /> Call us at 1-844-599-3355
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
